import { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { toast } from 'sonner'
import { OrganizationContext } from '../contexts/OrganizationContext'
import {
  Button,
  Badge,
  ConfirmDialog,
  DataView,
  DataTable,
  DropdownMenu,
  FormField,
  ModalShell,
  PageHeader,
  PageLayout,
  PageToolbar,
  SearchField,
  StatePanel,
  StatusBadge,
} from '../components/common'
import api from '../services/api'
import FormBuilder from '../components/FormBuilder'
import FormValidator from '../components/FormValidator'
import styles from './FormsPage.module.css'

function formatDate(value) {
  if (!value) return 'Unavailable'
  return new Date(value).toLocaleDateString()
}

function FormsPage() {
  const { currentOrg } = useContext(OrganizationContext)
  const [forms, setForms] = useState([])
  const [components, setComponents] = useState([])
  const [componentsError, setComponentsError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedForm, setSelectedForm] = useState(null)
  const [editingForm, setEditingForm] = useState(null)
  const [showBuilder, setShowBuilder] = useState(false)
  const [showValidatorDialog, setShowValidatorDialog] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null)
  const [showClonePrompt, setShowClonePrompt] = useState(null)
  const [showComponentReference, setShowComponentReference] = useState(false)
  const [cloneName, setCloneName] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const fetchForms = useCallback(async () => {
    if (!currentOrg) return

    try {
      setLoading(true)
      setError(null)
      const response = await api.get(`/${currentOrg.slug}/forms`)
      setForms(response.data.configurations || [])
    } catch (err) {
      setError('Failed to load forms')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [currentOrg])

  const fetchComponents = useCallback(async () => {
    if (!currentOrg) return

    try {
      setComponentsError(false)
      const response = await api.get(`/${currentOrg.slug}/forms/components`)
      setComponents(response.data.components || [])
    } catch {
      setComponentsError(true)
    }
  }, [currentOrg])

  useEffect(() => {
    fetchForms()
    fetchComponents()
  }, [fetchForms, fetchComponents])

  const closeBuilder = () => {
    setShowBuilder(false)
    setEditingForm(null)
  }

  const handleSaveForm = async (formData) => {
    try {
      if (editingForm) {
        await api.patch(`/${currentOrg.slug}/forms/${editingForm.id}`, formData)
        await fetchForms()
      } else {
        const response = await api.post(`/${currentOrg.slug}/forms`, formData)
        setForms((currentValue) => [...currentValue, response.data])
      }

      closeBuilder()
      toast.success(editingForm ? 'Form updated' : 'Form created')
    } catch (err) {
      throw new Error(err.response?.data?.error || 'Failed to save form')
    }
  }

  const handleDeleteForm = async () => {
    const formId = showDeleteConfirm
    setShowDeleteConfirm(null)

    try {
      await api.delete(`/${currentOrg.slug}/forms/${formId}`)
      setForms((currentValue) => currentValue.filter((form) => form.id !== formId))
    } catch (err) {
      toast.error(`Failed to delete form: ${err.response?.data?.error || err.message}`)
    }
  }

  const handleActivateForm = useCallback(async (formId) => {
    try {
      await api.post(`/${currentOrg.slug}/forms/${formId}/activate`)
      await fetchForms()
    } catch (err) {
      toast.error(`Failed to activate form: ${err.response?.data?.error || err.message}`)
    }
  }, [currentOrg, fetchForms])

  const handleCloneForm = useCallback((formId) => {
    setShowClonePrompt(formId)
    setCloneName('')
  }, [])

  const confirmCloneForm = async () => {
    if (!cloneName.trim()) return

    const formId = showClonePrompt
    setShowClonePrompt(null)
    try {
      const response = await api.post(`/${currentOrg.slug}/forms/${formId}/clone`, { name: cloneName.trim() })
      setForms((currentValue) => [...currentValue, response.data])
      toast.success('Form cloned')
    } catch (err) {
      toast.error(`Failed to clone form: ${err.response?.data?.error || err.message}`)
    }
  }

  const handleTestValidation = useCallback((form) => {
    setSelectedForm(form)
    setShowValidatorDialog(true)
  }, [])

  const groupedComponents = useMemo(() => {
    return components.reduce((groups, component) => {
      const key = component.category || 'General'
      if (!groups[key]) {
        groups[key] = []
      }
      groups[key].push(component)
      return groups
    }, {})
  }, [components])

  const filteredForms = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase()
    if (!query) return forms
    return forms.filter((form) => (
      form.name?.toLocaleLowerCase().includes(query)
      || form.form_type?.toLocaleLowerCase().includes(query)
      || (form.is_active ? 'active' : 'draft').includes(query)
    ))
  }, [forms, searchTerm])

  const columns = useMemo(() => ([
    {
      key: 'name',
      label: 'Form',
      sortable: true,
      render: (row) => (
        <div className={styles.formNameCell}>
          <strong>{row.name}</strong>
          <span>{row.form_type}</span>
        </div>
      ),
    },
    {
      key: 'fields',
      label: 'Fields',
      render: (row) => (
        <span className={styles.metaValue}>
          {Object.keys(row.schema?.properties || {}).length}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (row) => (
        <span className={styles.metaValue}>{formatDate(row.created_at)}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <StatusBadge status={row.is_active ? 'success' : 'default'}>
          {row.is_active ? 'Active' : 'Draft'}
        </StatusBadge>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className={styles.actionGroup}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setEditingForm(row)
              setShowBuilder(true)
            }}
          >
            Edit
          </Button>
          <DropdownMenu
            triggerLabel={`More actions for ${row.name}`}
            items={[
              { key: 'test', label: 'Test', onClick: () => handleTestValidation(row) },
              { key: 'activate', label: 'Activate', onClick: () => handleActivateForm(row.id), hidden: row.is_active },
              { key: 'clone', label: 'Clone', onClick: () => handleCloneForm(row.id) },
              { key: 'delete', label: 'Delete', onClick: () => setShowDeleteConfirm(row.id), danger: true },
            ]}
          />
        </div>
      ),
    },
  ]), [handleActivateForm, handleCloneForm, handleTestValidation])

  return (
    <PageLayout size="wide">
      <PageHeader
        title="Forms"
        secondaryActions={<Button variant="secondary" onClick={() => setShowComponentReference(true)} disabled={!currentOrg}>Component reference</Button>}
        primaryAction={<Button onClick={() => setShowBuilder(true)} disabled={!currentOrg}>Create form</Button>}
      />

      <DataView
        toolbar={(
          <PageToolbar
            label="Form controls"
            search={<SearchField value={searchTerm} onChange={setSearchTerm} label="Search forms" placeholder="Search forms…" />}
            actions={<Badge compact>{filteredForms.length} {filteredForms.length === 1 ? 'form' : 'forms'}</Badge>}
          />
        )}
        loading={Boolean(currentOrg) && loading}
        error={error ? new Error(error) : null}
        restricted={!currentOrg}
        items={filteredForms}
        filtered={Boolean(searchTerm.trim())}
        emptyTitle="No forms yet"
        emptyDescription="Create a form before attaching it to an operator workflow."
        searchEmptyTitle="No forms match your search"
        onRetry={fetchForms}
        onClear={() => setSearchTerm('')}
      >
        <DataTable
          data={filteredForms}
          columns={columns}
          paginate
          pageSize={10}
          density="compact"
          label="Forms"
        />
      </DataView>

      <ModalShell
        open={showComponentReference}
        title="Component reference"
        description="Available field types and schema properties."
        size="lg"
        onClose={() => setShowComponentReference(false)}
      >
        {componentsError ? (
          <StatePanel state="error" title="Component reference is unavailable" onRetry={fetchComponents} compact />
        ) : Object.keys(groupedComponents).length === 0 ? (
          <StatePanel state="empty" title="No component definitions available" compact />
        ) : (
          <div className={styles.referenceStack}>{Object.entries(groupedComponents).map(([category, categoryComponents]) => (
            <div key={category} className={styles.referenceSection}>
              <h3 className={styles.referenceTitle}>{category}</h3>
              <div className={styles.referenceGrid}>
                {categoryComponents.map((component) => (
                  <div key={component.type} className={styles.referenceCard}>
                    <div className={styles.referenceHeader}>
                      <strong>{component.label}</strong>
                      <code>{component.type}</code>
                    </div>
                    <p>{component.description}</p>
                    {component.properties && Object.keys(component.properties).length > 0 ? (
                      <div className={styles.referenceProperties}>
                        {Object.keys(component.properties).slice(0, 3).map((key) => (
                          <span key={key}>{key}</span>
                        ))}
                        {Object.keys(component.properties).length > 3 ? (
                          <span>+{Object.keys(component.properties).length - 3} more</span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ))}</div>
        )}
      </ModalShell>

      <ModalShell
        open={!!showClonePrompt}
        title="Clone form"
        description="Create a copy with a new name so the current version stays intact."
        onClose={() => setShowClonePrompt(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowClonePrompt(null)}>
              Cancel
            </Button>
            <Button onClick={confirmCloneForm} disabled={!cloneName.trim()}>
              Clone form
            </Button>
          </>
        }
      >
        <FormField
          label="New form name"
          value={cloneName}
          onChange={(event) => setCloneName(event.target.value)}
          placeholder="Enter a name for the cloned form"
          autoFocus
          compact
        />
      </ModalShell>

      {showBuilder ? (
        <FormBuilder
          components={components}
          initialForm={editingForm}
          onSave={handleSaveForm}
          onClose={closeBuilder}
        />
      ) : null}

      {showValidatorDialog && selectedForm ? (
        <FormValidator
          form={selectedForm}
          organizationSlug={currentOrg.slug}
          onClose={() => {
            setShowValidatorDialog(false)
            setSelectedForm(null)
          }}
        />
      ) : null}

      <ConfirmDialog
        open={!!showDeleteConfirm}
        title="Delete form"
        message="Delete this form configuration? This action cannot be undone."
        variant="danger"
        confirmLabel="Delete"
        onConfirm={handleDeleteForm}
        onCancel={() => setShowDeleteConfirm(null)}
      />
    </PageLayout>
  )
}

export default FormsPage
